if (!customElements.get('twa-artist-deck')) {
  customElements.define('twa-artist-deck', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.track = this.querySelector('[data-track]');
      this.cards = [...this.track.children];
      this.index = 0;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.classList.add('is-card-deck');
      this.querySelectorAll('[data-carousel-prev], [data-carousel-next]').forEach(button => {
        button.hidden = this.cards.length < 2;
        button.addEventListener('click', () => this.advance(button.hasAttribute('data-carousel-prev') ? -1 : 1), options);
      });
      this.track.addEventListener('click', event => {
        if (performance.now() < (this.ignoreClickUntil || 0)) return;
        if (this.track.contains(event.target)) this.advance(1);
      }, options);
      this.track.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.advance(event.key === 'ArrowLeft' ? -1 : 1);
      }, options);
      this.track.addEventListener('pointerdown', event => {
        if (this.busy || !event.isPrimary || event.button !== 0 || this.cards.length < 2) return;
        this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, distance: 0 };
        this.track.setPointerCapture(event.pointerId);
      }, options);
      this.track.addEventListener('pointermove', event => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        const distance = event.clientX - this.drag.x;
        if (!this.drag.moved && Math.abs(distance) < 6) return;
        if (!this.drag.moved && Math.abs(event.clientY - this.drag.y) > Math.abs(distance)) {
          this.endDrag(true);
          return;
        }
        this.drag.moved = true;
        this.drag.distance = distance;
        const front = this.cards[this.index];
        front.style.transition = 'none';
        front.style.transform = this.motion.matches ? 'none' : `translateX(${distance}px) rotate(${distance / 30}deg)`;
      }, options);
      this.track.addEventListener('pointerup', () => this.endDrag(false), options);
      this.track.addEventListener('pointercancel', () => this.endDrag(true), options);
      this.render();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.animation?.cancel();
    }

    endDrag(cancelled) {
      if (!this.drag) return;
      const { id, distance, moved } = this.drag;
      this.drag = null;
      if (this.track.hasPointerCapture(id)) this.track.releasePointerCapture(id);
      if (moved) this.ignoreClickUntil = performance.now() + 400;
      const threshold = Math.min(70, this.track.clientWidth * 0.2);
      if (!cancelled && Math.abs(distance) > threshold) this.advance(distance < 0 ? 1 : -1);
      else this.render();
    }

    render(except) {
      this.cards.forEach((card, index) => {
        if (card === except) return;
        const slot = (index - this.index + this.cards.length) % this.cards.length;
        card.hidden = slot > 3;
        card.inert = slot !== 0;
        card.setAttribute('aria-hidden', String(slot !== 0));
        card.style.transition = '';
        card.style.zIndex = 50 - slot;
        card.style.opacity = slot > 3 ? 0 : 1 - slot * 0.1;
        card.style.transform = slot === 0 ? 'translateX(0) rotate(0deg)' :
          `translate(${slot * 17}px, ${slot * 11}px) scale(${1 - slot * 0.045}) rotate(${slot * 2.4}deg)`;
      });
      this.querySelector('[data-carousel-counter]').textContent = `${this.index + 1} / ${this.cards.length}`;
    }

    async advance(direction) {
      if (this.busy || this.cards.length < 2) return;
      this.busy = true;
      const outgoing = this.cards[this.index];
      const restoreFocus = outgoing.contains(document.activeElement);
      const from = getComputedStyle(outgoing).transform;
      this.index = (this.index + direction + this.cards.length) % this.cards.length;
      const incoming = this.cards[this.index];
      this.render(direction > 0 ? outgoing : incoming);
      const moving = direction > 0 ? outgoing : incoming;
      moving.hidden = false;
      moving.inert = true;
      moving.style.transition = 'none';
      moving.style.zIndex = 60;
      const away = `translateX(-115%) rotate(-18deg)`;
      if (!this.motion.matches) {
        this.animation = moving.animate(direction > 0 ? [
          { transform: from, opacity: 1 }, { transform: away, opacity: 0 },
        ] : [
          { transform: away, opacity: 0 }, { transform: 'translateX(0) rotate(0deg)', opacity: 1 },
        ], { duration: 420, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'forwards' });
        try { await this.animation.finished; } catch { /* Removed in the theme editor. */ }
      }
      if (!this.isConnected) return;
      this.render();
      this.animation?.cancel();
      this.animation = null;
      this.busy = false;
      if (restoreFocus) incoming.querySelector('button')?.focus({ preventScroll: true });
    }
  });
}
